import React, { useEffect } from 'react';
import { useInView } from 'framer-motion';
import { useRef } from 'react';
import { motion } from 'framer-motion';

// Reusable animation component that triggers when element is in view
const ScrollAnimation = ({ 
  children, 
  animation = "fadeIn", // Default animation type
  delay = 0,
  duration = 0.5,
  threshold = 0.1, // Reduced default threshold for better mobile responsiveness
  once = true, // Only animate once
  className = "",
  ...props 
}) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { 
    once, 
    amount: threshold,
    margin: "0px 0px -50px 0px" // Negative bottom margin to trigger earlier
  });

  // Animation variants - enhanced for better mobile visibility
  const variants = {
    // Fade in
    fadeIn: {
      hidden: { opacity: 0 },
      visible: { opacity: 1, transition: { duration, delay } }
    },
    // Slide up and fade in
    slideUp: {
      hidden: { opacity: 0, y: 30 }, // Reduced distance for mobile
      visible: { opacity: 1, y: 0, transition: { duration, delay } }
    },
    // Slide in from left
    slideInLeft: {
      hidden: { opacity: 0, x: -30 }, // Reduced distance for mobile
      visible: { opacity: 1, x: 0, transition: { duration, delay } }
    },
    // Slide in from right
    slideInRight: {
      hidden: { opacity: 0, x: 30 }, // Reduced distance for mobile
      visible: { opacity: 1, x: 0, transition: { duration, delay } }
    },
    // Scale up
    scaleUp: {
      hidden: { opacity: 0, scale: 0.9 }, // Less dramatic scale for mobile
      visible: { opacity: 1, scale: 1, transition: { duration, delay } }
    },
    // Pop effect
    pop: {
      hidden: { opacity: 0, scale: 0.8 }, // Less dramatic scale for mobile
      visible: { 
        opacity: 1, 
        scale: 1, 
        transition: { 
          duration: duration * 0.8, 
          delay,
          type: "spring",
          stiffness: 300,
          damping: 15
        } 
      }
    },
    // Staggered appearance for child elements
    stagger: {
      hidden: { opacity: 0 },
      visible: {
        opacity: 1,
        transition: {
          delay,
          staggerChildren: 0.1,
          delayChildren: 0.2,
        }
      }
    },
    // Item for staggered animations
    staggerItem: {
      hidden: { opacity: 0, y: 15 }, // Reduced distance for mobile
      visible: { opacity: 1, y: 0, transition: { duration: 0.5 } }
    }
  };

  // Select the animation variant
  const selectedVariant = variants[animation] || variants.fadeIn;

  return (
    <motion.div
      ref={ref}
      initial="hidden"
      animate={isInView ? "visible" : "hidden"}
      variants={selectedVariant}
      className={className}
      {...props}
    >
      {children}
    </motion.div>
  );
};

export default ScrollAnimation; 